import * as d3 from 'd3'
import * as d3Hierarchy from 'd3-hierarchy'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import {
  GROUP_NODE_SIZE_COLLAPSED,
  NODE_SIZE,
} from 'modules/people_chart/components/GroupChart/Node'
import type { BoundingBox } from 'modules/people_chart/lib/BoundingBox'
import type { Coordinates } from 'modules/people_chart/lib/Coordinates'
import { HierarchyLink } from 'modules/people_chart/lib/Hierarchy'
import type { PointNode } from 'modules/people_chart/lib/PointNode'

interface ReturnType {
  tree: PointNode<HierarchyLink>[]
  buildTree: (data: d3.HierarchyNode<HierarchyLink>) => PointNode<HierarchyLink>[]
  links: d3Hierarchy.HierarchyPointLink<HierarchyLink>[]
  treeBoundary: BoundingBox
  findFirstNode: (nodeIds: string[]) => string | undefined
}

const useTree = (hierarchyData: HierarchyLink[], width: number, teamsIds: string[]): ReturnType => {
  const initialCoordinates = useUnsafeMemo<Coordinates>(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE
    const initialX = width / 2 - nodeWidth / 2
    const initialY = nodeHeight / 2

    return [initialX, initialY]
  }, [width])

  const pruneTree = useUnsafeCallback((ids: Set<string>, node: d3.HierarchyNode<HierarchyLink>) => {
    if (node.id !== '0' && node.id && !ids.has(node.id)) {
      return null
    }

    if (node.children) {
      node.children = node.children
        .map((child) => pruneTree(ids, child)) // Recursively prune children
        .filter(Boolean) // Filter out null values (nodes to be removed)
    }
    // If the node has no children, remove the 'children' property
    if (node.children && node.children.length === 0) {
      delete node.children
    }

    return node
  }, [])

  const hierarchy = useUnsafeMemo(() => {
    try {
      const tree = d3Hierarchy.stratify<HierarchyLink>()(hierarchyData)
      const teamsIdsSet = new Set(teamsIds)
      return pruneTree(teamsIdsSet, tree)
    } catch (err) {}
  }, [hierarchyData, pruneTree, teamsIds])

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const links = (hierarchy?.links() as d3Hierarchy.HierarchyPointLink<HierarchyLink>[]) || []

  const buildTree = useUnsafeCallback(
    (root: d3.HierarchyNode<HierarchyLink>) => {
      const SIBLINGS_SEPARATION = 1.46 // 140px
      const [nodeWidth, nodeHeight] = NODE_SIZE
      const treeLayout = d3Hierarchy
        .tree<HierarchyLink>()
        .nodeSize([nodeWidth, nodeHeight])
        .separation((nodeA, nodeB) => {
          return SIBLINGS_SEPARATION
        })
      const [initialX, initialY] = initialCoordinates
      // the first parent is the org-root we don't want to show it
      const yWithoutGhostParent = initialY - nodeHeight
      const tree = treeLayout(root).descendants()
      tree.forEach((node) => {
        node.x = node.x + initialX
        node.y = node.y + yWithoutGhostParent
      })

      return tree
    },
    [initialCoordinates]
  )

  const tree = useUnsafeMemo(() => (hierarchy ? buildTree(hierarchy) : []), [hierarchy, buildTree])

  // this functions is used to calculate the boundary of the tree
  // iterates the nodes to find the one with the minimum and maximum x
  // and the minimum (without the organization node) and maximum y
  // it sums the node_with and node_height to the max x and y to get the boundary
  const treeBoundary = useUnsafeMemo(() => {
    const [nodeWidth, nodeHeight] = GROUP_NODE_SIZE_COLLAPSED
    const minX = d3.min(tree, (d) => d.x) || 0
    const maxX = d3.max(tree, (d) => d.x) || 0
    const minY = d3.min(tree, (d) => (d.data.type === 'organization' ? NaN : d.y + 0)) || 0
    const maxY = d3.max(tree, (d) => d.y + nodeHeight) || 0

    return {
      x0: minX,
      x1: maxX + nodeWidth,
      y0: minY,
      y1: maxY + nodeHeight,
    }
  }, [tree])

  const findFirstNode = useUnsafeCallback(
    (nodeIds: string[]): string | undefined => {
      const leastDepthId = tree.reduce(
        (
          minDepthNode: d3.HierarchyPointNode<HierarchyLink> | null,
          descendant: d3.HierarchyPointNode<HierarchyLink>
        ) => {
          if (nodeIds.includes(descendant.id || '')) {
            if (!minDepthNode || descendant.depth < minDepthNode.depth) {
              return descendant
            }
          }
          return minDepthNode
        },
        null
      )

      return leastDepthId?.id
    },
    [tree]
  )

  return {
    tree,
    buildTree,
    links,
    treeBoundary,
    findFirstNode,
  }
}

export default useTree
